import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseDataListResponsiveOptions {
  mobileBreakpoint?: number;
}

export interface UseDataListResponsiveResult {
  isMobile: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  containerWidth: number;
}

/**
 * Hook for responsive behavior in DataListMolecule.
 * Uses container width detection (not viewport) for better component-level responsiveness.
 */
export const useDataListResponsive = (
  options: UseDataListResponsiveOptions = {}
): UseDataListResponsiveResult => {
  const { mobileBreakpoint = 768 } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    // Initial measurement
    updateWidth();

    // Set up ResizeObserver for container width changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateWidth]);

  const isMobile = containerWidth > 0 && containerWidth < mobileBreakpoint;

  return {
    isMobile,
    containerRef,
    containerWidth,
  };
};

export default useDataListResponsive;
